import React, { useCallback, useState } from 'react';
import { Table, Button, Badge } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';

import { useToast } from '../../../hooks/toast';
import { IMedicamento } from '../../../hooks/receita';
import MedicamentoForm from '../MedicamentoForm';

import {
  Container,
  TableContainer,
  ActionButtons,
  ControladoBadge,
} from './styles';

interface Props {
  medicamentos: IMedicamento[];
  receitaStatus: 'Criada' | 'Emitida' | 'Cancelada';
  receitaCodigo: number;
  podeEditar?: boolean;
  onUpdateMedicamento: (medicamento: IMedicamento) => void;
  onDeleteMedicamento: (medCodigo: number) => void;
}

const MedicamentoLista: React.FC<Props> = ({
  medicamentos,
  receitaStatus,
  receitaCodigo,
  podeEditar = true,
  onUpdateMedicamento,
  onDeleteMedicamento,
}) => {
  const { addToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingMedicamento, setEditingMedicamento] = useState<
    IMedicamento | undefined
  >(undefined);

  const canEdit = receitaStatus === 'Criada' && podeEditar;

  const handleEdit = useCallback(
    (medicamento: IMedicamento) => {
      if (!canEdit) {
        addToast({
          type: 'error',
          title: 'Não permitido',
          description:
            'Não é possível editar medicamentos de uma receita que não está com status "Criada"',
        });
        return;
      }
      setEditingMedicamento(medicamento);
      setShowModal(true);
    },
    [addToast, canEdit],
  );

  const handleDelete = useCallback(
    (medCodigo: number) => {
      if (!canEdit) {
        addToast({
          type: 'error',
          title: 'Não permitido',
          description:
            'Não é possível remover medicamentos de uma receita que não está com status "Criada"',
        });
        return;
      }

      if (window.confirm('Tem certeza que deseja remover este medicamento?')) {
        onDeleteMedicamento(medCodigo);
      }
    },
    [addToast, canEdit, onDeleteMedicamento],
  );

  const handleSave = useCallback(
    (medicamento: IMedicamento) => {
      onUpdateMedicamento(medicamento);
      setShowModal(false);
      setEditingMedicamento(undefined);
    },
    [onUpdateMedicamento],
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingMedicamento(undefined);
  }, []);

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Medicamentos ({medicamentos.length})</h5>
        {canEdit && (
          <Button
            variant="success"
            size="sm"
            onClick={() => {
              setEditingMedicamento(undefined);
              setShowModal(true);
            }}
          >
            <FaEdit size={14} /> Novo Medicamento
          </Button>
        )}
      </div>

      {medicamentos.length === 0 ? (
        <div className="text-center text-muted py-4">
          <p className="mb-0">Nenhum medicamento adicionado</p>
          {canEdit && (
            <small>Clique em &apos;Novo Medicamento&apos; para adicionar</small>
          )}
        </div>
      ) : (
        <TableContainer>
          <Table striped bordered hover size="sm" className="mb-0">
            <thead className="thead-light">
              <tr>
                <th style={{ width: '30%' }}>Medicamento</th>
                <th style={{ width: '20%' }}>Quantidade</th>
                <th style={{ width: '30%' }}>Posologia</th>
                <th style={{ width: '10%' }}>Controlado</th>
                <th style={{ width: '10%' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {medicamentos.map(med => (
                <tr key={med.MedCodigo}>
                  <td className="font-weight-bold">{med.MedNome}</td>
                  <td>{med.MedQuantidade}</td>
                  <td className="text-truncate" style={{ maxWidth: '200px' }}>
                    {med.MedObservacao || '-'}
                  </td>
                  <td>
                    <ControladoBadge>
                      <Badge
                        variant={med.MedControlado ? 'warning' : 'secondary'}
                      >
                        {med.MedControlado ? 'Sim' : 'Não'}
                      </Badge>
                    </ControladoBadge>
                  </td>
                  <td>
                    <ActionButtons>
                      {canEdit && (
                        <>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(med)}
                            title="Editar"
                          >
                            <FaEdit size={14} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(med.MedCodigo)}
                            title="Excluir"
                          >
                            <FaTrash size={14} />
                          </Button>
                        </>
                      )}
                    </ActionButtons>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}

      <MedicamentoForm
        show={showModal}
        onClose={handleCloseModal}
        onSave={handleSave}
        receitaStatus={receitaStatus}
        initialData={editingMedicamento}
        isEditing={!!editingMedicamento}
      />
    </Container>
  );
};

export default MedicamentoLista;
